import { api } from '@/config/api';

export interface AuthUser {
  userUid: string;
  companyUid: string | null;
  role: 'super_admin' | 'company_admin' | 'company_user';
}
export interface AuthResponse { token: string; user: AuthUser; backupCodesRemaining?: number }

export type TwoFactorMethod = 'totp' | 'whatsapp';
/** Returned instead of a session when the account has two-step verification on. */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeId: string;
  method: TwoFactorMethod;
  destination?: string;
  resendAfterSeconds?: number;
  deliveryError?: string;
}
export interface TwoFactorStatus {
  enabled: boolean;
  method?: TwoFactorMethod;
  destination?: string;
  enabledAt?: string;
  backupCodesRemaining: number;
  whatsappAvailable: boolean;
}
export type TwoFactorSetup =
  | { setupId: string; method: 'totp'; secret: string; otpauthUri: string; qrSvg: string }
  | { setupId: string; method: 'whatsapp'; destination: string; resendAfterSeconds: number };

export const isTwoFactorChallenge = (value: unknown): value is TwoFactorChallenge =>
  !!value && typeof value === 'object' && (value as TwoFactorChallenge).twoFactorRequired === true;

export const authService = {
  loginWithPassword: async (email: string, password: string): Promise<AuthResponse | TwoFactorChallenge> =>
    (await api.post('/auth/login', { email, password })).data,
  verifyTwoFactor: async (challengeId: string, code: string): Promise<AuthResponse> =>
    (await api.post('/auth/2fa/verify', { challengeId, code }, { expectedStatuses: [400] })).data,
  resendTwoFactor: async (challengeId: string): Promise<{ destination?: string; resendAfterSeconds: number }> =>
    (await api.post('/auth/2fa/resend', { challengeId }, { expectedStatuses: [400] })).data,
  twoFactorStatus: async (): Promise<TwoFactorStatus> => (await api.get('/auth/2fa', { expectedStatuses: [404] })).data,
  startTwoFactorSetup: async (method: TwoFactorMethod, phone?: string): Promise<TwoFactorSetup> =>
    (await api.post('/auth/2fa/setup', { method, ...(phone ? { phone } : {}) })).data,
  confirmTwoFactorSetup: async (setupId: string, code: string): Promise<{ enabled: true; method: TwoFactorMethod; backupCodes: string[] }> =>
    (await api.post('/auth/2fa/setup/confirm', { setupId, code }, { expectedStatuses: [400] })).data,
  disableTwoFactor: async (password: string): Promise<{ enabled: false }> =>
    (await api.post('/auth/2fa/disable', { password }, { expectedStatuses: [400] })).data,
  renewBackupCodes: async (password: string): Promise<{ backupCodes: string[] }> =>
    (await api.post('/auth/2fa/backup-codes', { password }, { expectedStatuses: [400] })).data,
  resetTeammateTwoFactor: async (userUid: string): Promise<{ enabled: false }> =>
    (await api.post(`/auth/2fa/users/${encodeURIComponent(userUid)}/reset`)).data,
};
